import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { format } from 'date-fns-tz'
import { type Locale } from 'date-fns'
import { ko, enUS } from 'date-fns/locale'
import i18n from '../i18n'
import { STABLECOIN_BASE_TOKENS } from '../constants/currency'
import { getDecimalsForCurrency } from './currency-registry'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// ============= 날짜 유틸리티 함수 =============

// 브라우저 시간대 감지
export function getLocalTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

// 로컬 시간대 기준으로 날짜의 시작 시간 (00:00:00) 설정
export function getLocalDateStart(date: Date): Date {
  const localDate = new Date(date)
  localDate.setHours(0, 0, 0, 0)
  return localDate
}

// 로컬 시간대 기준으로 날짜의 끝 시간 (23:59:59.999) 설정
export function getLocalDateEnd(date: Date): Date {
  const localDate = new Date(date)
  localDate.setHours(23, 59, 59, 999)
  return localDate
}

// ============= 날짜 범위 프리셋 유틸리티 =============

/** 날짜 범위 프리셋 타입 */
export type DateRangePreset = 'today' | '7days' | '30days' | '180days'

/** 날짜 범위 결과 */
export interface DateRange {
  start: Date
  end: Date
}

/** 날짜 범위 오프셋 상수 */
const DATE_RANGE_OFFSETS: Record<DateRangePreset, number> = {
  today: 0,
  '7days': 6,
  '30days': 29,
  '180days': 179,
}

/**
 * 날짜 범위 프리셋에 따른 시작/종료 날짜 계산
 * @param preset 날짜 범위 프리셋 ('today', '7days', '30days', '180days')
 * @returns 시작일과 종료일 (로컬 시간대 기준)
 *
 * @example
 * ```ts
 * const { start, end } = getDateRangePreset('7days')
 * // start: 6일 전 00:00:00
 * // end: 오늘 23:59:59.999
 * ```
 */
export function getDateRangePreset(preset: DateRangePreset): DateRange {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  const offset = DATE_RANGE_OFFSETS[preset] ?? DATE_RANGE_OFFSETS['7days']
  const startDate = new Date(today)
  startDate.setDate(today.getDate() - offset)

  return {
    start: getLocalDateStart(startDate),
    end: getLocalDateEnd(today),
  }
}

/**
 * 기본 날짜 범위 생성 (최근 7일)
 * @returns 시작일과 종료일 (로컬 시간대 기준)
 */
export function getDefaultDateRange(): DateRange {
  return getDateRangePreset('7days')
}

// YYYY-MM-DD 문자열을 Date 객체로 변환 (시간대 이슈 방지)
export function createDateFromString(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number)
  return new Date(year, month - 1, day) // month는 0부터 시작
}

// Date 객체를 YYYY-MM-DD 형식 문자열로 변환
export function formatDateForInput(date: Date): string {
  const year = date.getFullYear()
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * 네트워크 특정 통화 코드에서 기본 토큰 추출
 * 예: TTRON_USDT → USDT, TRON_USDC → USDC
 * 네트워크 특정 코드가 아니면 원본 반환
 */
function extractBaseToken(currency: string): string {
  // 네트워크_토큰 형식 확인 (예: TRON_USDT, TTRON_USDT, ETH_USDC)
  if (currency.includes('_')) {
    const parts = currency.split('_')
    const lastPart = parts[parts.length - 1]
    if (STABLECOIN_BASE_TOKENS.includes(lastPart)) {
      return lastPart
    }
  }
  return currency
}

/**
 * 암호화폐/스테이블코인 통화 코드인지 확인
 * - 네트워크 특정 코드: TTRON_USDT, TRON_USDC → true
 * - 기본 스테이블코인: USDT, USDC → true
 */
function isCryptoCurrency(currency: string): boolean {
  // 스테이블코인 기본 토큰인 경우
  if (STABLECOIN_BASE_TOKENS.includes(currency)) {
    return true
  }
  // 네트워크 특정 통화 코드인 경우
  if (currency.includes('_')) {
    const parts = currency.split('_')
    const lastPart = parts[parts.length - 1]
    return STABLECOIN_BASE_TOKENS.includes(lastPart)
  }
  return false
}

// 통화별 소수점 자릿수 가져오기 (레지스트리 우선, 하드코딩 fallback)
function getCurrencyDecimals(currency: string): number {
  return getDecimalsForCurrency(currency)
}

/**
 * i18n 언어 설정에 따른 로케일 반환
 */
function getLocaleForCurrency(): string {
  const lang = i18n.language
  // 언어별 로케일 매핑
  const localeMap: Record<string, string> = {
    ko: 'ko-KR',
    en: 'en-US',
    ja: 'ja-JP',
    zh: 'zh-CN',
  }
  return localeMap[lang] || 'en-US'
}

// 통화별 포맷팅 함수
export function formatCurrency(
  amount: number,
  currency: string = 'USD'
): string {
  const decimals = getCurrencyDecimals(currency)
  const locale = getLocaleForCurrency()

  // 암호화폐/스테이블코인 통화 코드인 경우 (예: TTRON_USDT, USDT)
  // Intl.NumberFormat에서 지원하지 않으므로 직접 포맷팅
  if (isCryptoCurrency(currency)) {
    const formattedAmount = amount.toLocaleString(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
    return `${formattedAmount} ${currency}`
  }

  const formatOptions: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }

  try {
    return new Intl.NumberFormat(locale, formatOptions).format(amount)
  } catch (error) {
    // 통화 코드가 지원되지 않는 경우 기본 포맷 사용
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`Unsupported currency code: ${currency}`, error)
    }
    const formattedAmount =
      decimals === 0
        ? Math.round(amount).toLocaleString(locale)
        : amount.toLocaleString(locale, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          })
    return `${formattedAmount} ${currency}`
  }
}

export function formatCurrencyWithoutSymbol(
  amount: number,
  currency: string
): string {
  const decimals = getCurrencyDecimalsV2(currency)
  const locale = getLocaleForCurrency()
  return amount.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

function getCurrencyDecimalsV2(currency: string): number {
  return getDecimalsForCurrency(currency)
}

/**
 * 암호화폐 금액 포맷팅
 * @param amount 금액
 * @param currency 통화 코드 (네트워크 정보 포함, 예: USDT_TRC20)
 */
export function formatCryptoCurrency(amount: number, currency: string): string {
  if (amount > 0) {
    return `${currency} ${amount}`
  }
  return ''
}

// 날짜 포맷팅 함수 (i18n 및 시간대 지원)
export function formatDate(
  date: string | Date,
  formatString = 'yyyy-MM-dd HH:mm:ss'
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  const localeMap: Record<string, Locale> = {
    ko: ko,
    en: enUS,
  }

  // 브라우저의 로컬 시간대 감지
  const timezone = getLocalTimeZone()

  // 시간대 정보 추가 (ISO offset 형식: +09:00)
  const formatWithTimezone = `${formatString} XXX`

  return format(dateObj, formatWithTimezone, {
    timeZone: timezone,
    locale: localeMap[i18n.language] || ko,
  })
}

// 상태별 색상 매핑
export function getStatusColor(status: string): string {
  const statusColors: Record<string, string> = {
    PENDING: 'text-orange-600 bg-orange-100',
    PROCESSING: 'text-blue-600 bg-blue-100',
    ON_HOLD: 'text-amber-700 bg-amber-100',
    COMPLETED: 'text-green-600 bg-green-100',
    FAILED: 'text-red-600 bg-red-100',
    CANCELLED: 'text-gray-600 bg-gray-100',
    PAID: 'text-green-600 bg-green-100',
    REFUNDED: 'text-purple-600 bg-purple-100',
    PARTIAL_REFUNDED: 'text-yellow-600 bg-yellow-100',
  }

  return statusColors[status] || 'text-gray-600 bg-gray-100'
}

// ============= JSON 유틸리티 함수 =============

/**
 * JSON 문자열을 안전하게 파싱
 * - 파싱 실패 시 기본값 반환
 * - 이미 객체인 경우 그대로 반환
 */
export function parseJSON<T = any>(value: any, defaultValue: T): T {
  // 이미 파싱된 객체/배열인 경우
  if (typeof value === 'object' && value !== null) {
    return value as T
  }

  // 문자열인 경우 파싱 시도
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as T
    } catch (error) {
      console.warn('JSON 파싱 실패:', error)
      return defaultValue
    }
  }

  // null, undefined 등
  return defaultValue
}

/**
 * JSON을 보기 좋게 포맷팅
 */
export function formatJSON(value: any): string {
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

// ============= 홍콩 시간대 (HKT) 유틸리티 =============

/**
 * 홍콩 시간(HKT)으로 날짜의 시작 시간(00:00:00)을 UTC로 변환
 * 예: 2026-01-15 HKT 00:00:00 → 2026-01-14 UTC 16:00:00
 * @param date - 사용자가 선택한 날짜 (로컬 시간대)
 * @returns UTC Date 객체
 */
export function getHKTDateStartAsUTC(date: Date): Date {
  // 선택된 날짜의 연, 월, 일 추출
  const year = date.getFullYear()
  const month = date.getMonth()
  const day = date.getDate()

  // UTC로 HKT 00:00:00 생성
  // HKT = UTC+8 이므로, UTC 16:00:00 (전날) = HKT 00:00:00
  const utc = new Date(Date.UTC(year, month, day, 0, 0, 0, 0))
  // UTC에서 8시간 빼기 (HKT 00:00 = UTC 전날 16:00)
  utc.setUTCHours(utc.getUTCHours() - 8)
  return utc
}

/**
 * 홍콩 시간(HKT)으로 날짜의 다음날 시작(00:00:00)을 UTC로 변환 (exclusive end)
 * 예: 2026-01-15 → HKT 2026-01-16 00:00:00 → UTC 2026-01-15 16:00:00
 * @param date - 사용자가 선택한 종료 날짜 (로컬 시간대)
 * @returns UTC Date 객체
 */
export function getHKTNextDayStartAsUTC(date: Date): Date {
  const nextDay = new Date(date)
  nextDay.setDate(nextDay.getDate() + 1)
  return getHKTDateStartAsUTC(nextDay)
}

/**
 * Truncate a blockchain address for display
 * e.g., "TNPeeaaFB7K9cmo4uQpcU32zGK8G1NYqeL" → "TNPeeaaF...1NYqeL"
 */
export function truncateAddress(
  addr: string | null | undefined,
  prefixLen = 8,
  suffixLen = 6
): string {
  if (!addr || addr.length <= prefixLen + suffixLen + 3) return addr || '-'
  return `${addr.slice(0, prefixLen)}...${addr.slice(-suffixLen)}`
}
